// Shared page, chrome and state strings for the public site, resolved for one
// sport. Grouped by page key; an entry's comment names the classic page (and
// the spot on it) the words first came from. A template reads
// `useLabels().ui.<page>.<key>` and never writes these words itself.
//
// Values follow audit TERMINOLOGY.md §2a-2c and §2e; the `// TERMINOLOGY §…`
// tags name the row. The words are in the site-core catalogue (#1118): a
// sentence whose noun changes with the sport is one message of the sport id
// there; a bare noun (Games, Per game) is the pack's term().

import type { TermFn } from './types'
import type siteCoreEn from '@/i18n/catalogs/site-core/en'

/** The site-core catalogue in the active language (#1063). */
type SiteCoreMessages = typeof siteCoreEn

export function buildUi(t: TermFn, sport: string, m: SiteCoreMessages) {
  const l = m.labels
  const r = l.record
  const mm = m.member

  return {
    common: {
      /** Schedule ErrorState heading. */
      errorTitle: m.common.errorTitle,
      /**
       * The text shown for a failed load (#420, TERMINOLOGY §2e last row).
       * Never the raw error ("API error: …", "Request timeout: /public/…"):
       * the page's friendly fallback when it has one, else the shared line.
       */
      errorMessage: (_err: unknown, fallback?: string): string => fallback || m.common.loadError,
      tbd: m.common.tbd,
      /** Schedule ErrorState button */
      retry: m.common.retry,
      /** Players, Teams, Venues */
      loading: m.common.loading,
      /** Generic not-found state (every template's NotFound, #312). */
      notFoundTitle: m.common.notFoundTitle,
      notFoundBody: m.common.notFoundBody,
      /** Eyebrow for the next game(s) (new templates). */
      nextUp: m.common.nextUp,
      /** A game whose start time has passed but has no score yet (the
       *  `scoreSoon` status kind). One shared wording for every template
       *  (team-lead, 2026-10-08): the full form is the status text itself;
       *  the short form is for cells under ~90px. No ellipsis. */
      awaitingResult: l.gameStatus.scoreSoon,
      awaitingResultShort: m.common.awaitingResultShort,
      /** The one friendly line for a failed load when a page has no more
       *  specific fallback (#420). Not shown by classic until the #420 fix. */
      loadError: m.common.loadError,
    },

    schedule: {
      /** Schedule */
      emptyTitle: l.schedule.emptyTitle,
      /** Schedule — TERMINOLOGY §2a */
      emptyBody: l.schedule.emptyBody(sport),
      /** Schedule — TERMINOLOGY §2a */
      subtitle: l.schedule.subtitle(sport),
      /** Schedule (fallback when the error has no message) — §2a */
      loadError: l.schedule.loadError,
      /** Schedule — TERMINOLOGY §2a */
      competitionLoadError: l.schedule.competitionLoadError,
      /** /schedule/:id for an unknown or unpublished competition (#418/#547
       *  follow-up). New state: no classic text today. */
      competitionNotFound: l.schedule.competitionNotFound,
      competitionNotFoundBody: l.schedule.competitionNotFoundBody,
      /** ScheduleRoundRobin section heading — TERMINOLOGY §2b */
      liveNowHeading: l.schedule.liveNowHeading,
      /** ScheduleRoundRobin */
      roundRobinEmptyTitle: l.schedule.roundRobinEmptyTitle,
      /** ScheduleRoundRobin — TERMINOLOGY §2a */
      roundRobinEmptyBody: l.schedule.roundRobinEmptyBody(sport),
      /** ScheduleRoundRobin "Matchday {n}" — TERMINOLOGY §2e (kept) */
      matchday: (n: number | string): string => l.schedule.matchday(n),
      /** ScheduleRoundRobin — TERMINOLOGY §2c */
      gameDetailsLink: l.schedule.gameDetailsLink(sport),
      /** ScheduleBracket */
      bracketEmptyTitle: l.schedule.bracketEmptyTitle,
      /** ScheduleBracket — TERMINOLOGY §2a */
      bracketEmptyBody: l.schedule.bracketEmptyBody,
      /** #652: the season picker's label and its "today's view" option. */
      seasonPicker: l.schedule.seasonPicker,
      currentSeason: l.schedule.currentSeason,
      currentSeasons: l.schedule.currentSeasons,
    },

    home: {
      /** Home default title of the "upcoming" section (tenants
       *  override it with section_overrides.upcoming.title). PAIR:
       *  admin-v2/data/site/design.ts defaultTitle. TERMINOLOGY §2c */
      upcomingTitle: l.home.upcomingTitle(sport),
      /** Home — TERMINOLOGY §2c */
      upcomingEmpty: l.home.upcomingEmpty(sport),
      /** Home stream badge (after the 📺) — TERMINOLOGY §2b */
      streamBadge: (isLive: boolean): string => (isLive ? l.home.streamLive : l.home.streamed),
    },

    game: {
      /** GameDetail — TERMINOLOGY §2c */
      notFoundTitle: l.game.notFoundTitle(sport),
      /** GameDetail — TERMINOLOGY §2c */
      notFoundBody: l.game.notFoundBody(sport),
      /** GameDetail — TERMINOLOGY §2c */
      sponsoredBy: l.game.sponsoredBy(sport),
      /** GameDetail — TERMINOLOGY §2c */
      noBoxScore: l.game.noBoxScore(sport),
      /** GameDetail — TERMINOLOGY §2c */
      notStartedTitle: l.game.notStartedTitle(sport),
      /** The game page's name in new templates ("Game center" / "Match center"). */
      centerTitle: l.game.centerTitle(sport),
      /** GameDetail share modal title — TERMINOLOGY §2c */
      shareTitle: l.game.shareTitle,
      /** GameDetail — TERMINOLOGY §2e (founder call) */
      scorebookTitle: l.game.scorebookTitle,
      /** GameDetail — TERMINOLOGY §2e (founder call) */
      scorebookSubtitle: l.game.scorebookSubtitle,
    },

    player: {
      /** PlayerDetail,106 share-card context — TERMINOLOGY §2e */
      shareLifetime: r.playerShareLifetime,
      /** PlayerDetail */
      shareSeasonFallback: r.shareSeasonFallback,
      /** PlayerDetail — TERMINOLOGY §2a */
      shareCompetitionFallback: r.shareCompetitionFallback,
      /** PlayerDetail,104 "{season or competition name} Stats" */
      shareNamed: (name: string): string => r.shareNamed(name),
      /** PlayerDetail — TERMINOLOGY §2a */
      competitionSelectLabel: t('competition'),
      /** PlayerDetail — TERMINOLOGY §2a */
      competitionSelectPlaceholder: r.competitionSelectPlaceholder,
      /** PlayerDetail */
      emptyLifetime: l.player.emptyLifetime,
      /** PlayerDetail */
      seasonPick: l.player.seasonPick,
      /** PlayerDetail */
      seasonEmpty: l.player.seasonEmpty,
      /** PlayerDetail — TERMINOLOGY §2a */
      competitionPick: r.competitionPick,
      /** PlayerDetail — TERMINOLOGY §2a */
      competitionEmpty: l.player.competitionEmpty,
      /** PlayerDetail games-played tile — TERMINOLOGY §2c */
      gamesTile: t('games'),
      /** PlayerDetail — TERMINOLOGY §2c */
      gameLogTitle: l.player.gameLogTitle(sport),
      /** PlayerDetail game-log scope when not filtered to a season — §2c */
      gameLogAllScope: l.player.gameLogAllScope(sport),
    },

    team: {
      /** TeamDetail,62 share-card context — TERMINOLOGY §2e */
      shareLifetime: r.teamShareLifetime,
      /** TeamDetail */
      shareSeasonFallback: r.shareSeasonFallback,
      /** TeamDetail — TERMINOLOGY §2a */
      shareCompetitionFallback: r.shareCompetitionFallback,
      /** TeamDetail,60 */
      shareNamed: (name: string): string => r.shareNamed(name),
      /** TeamDetail — TERMINOLOGY §2a */
      competitionSelectLabel: t('competition'),
      /** TeamDetail — TERMINOLOGY §2a */
      competitionSelectPlaceholder: r.competitionSelectPlaceholder,
      /** TeamDetail — TERMINOLOGY §2a */
      competitionPick: r.competitionPick,
      /** TeamDetail section heading — TERMINOLOGY §2c */
      gamesHeading: t('games'),
    },

    stats: {
      /** Stats season filter "all" option — TERMINOLOGY §2e */
      allTime: l.stats.allTime,
      /** Stats — TERMINOLOGY §2a */
      competitionFilterLabel: t('competition'),
      /** Stats — TERMINOLOGY §2a */
      allCompetitions: l.stats.allCompetitions,
      /** Stats — TERMINOLOGY §2c */
      minGamesPlayed: l.stats.minGamesPlayed(sport),
      /** Stats spotlight card when a category has no leader yet (#420). */
      noLeader: l.stats.noLeader,
      /** Stats — TERMINOLOGY §2c */
      perGameToggle: t('perGame'),
      /** Stats */
      totalsToggle: l.stats.totalsToggle,
    },

    gallery: {
      /** Gallery default intro (tenants override with copy.gallery_intro) — §2a */
      intro: (tenantName: string): string => l.gallery.intro(tenantName, sport),
      /** Gallery — TERMINOLOGY §2a (PAIR: admin media filter already says Competition) */
      allCompetitions: l.stats.allCompetitions,
    },

    sale: {
      /** SalePage — TERMINOLOGY §2a */
      competitionEyebrow: t('competition'),
      /** SalePage — TERMINOLOGY §2a */
      competitionLink: l.sale.competitionLink,
      /** SalePage — TERMINOLOGY §2c */
      gameEyebrow: t('game'),
      /** SalePage — TERMINOLOGY §2c */
      gameLink: l.sale.gameLink(sport),
    },

    venue: {
      /** VenueDetail (sport-pack venueLabel fix is bug #413 territory, §2d) */
      spacesHeading: l.venue.spacesHeading,
      /** VenueDetail — TERMINOLOGY §2c */
      gamesHeading: l.venue.gamesHeading(sport),
      /** VenueDetail — TERMINOLOGY §2c */
      gamesEmpty: l.venue.gamesEmpty(sport),
    },

    venues: {
      /** Venues — TERMINOLOGY §2c */
      subtitle: l.venue.venuesSubtitle(sport),
    },

    live: {
      /** components/live-stats/LiveBoxScore (§2d, value via pack terms) */
      onFieldHeading: t('onField'),
    },

    /** Relative-day words for upcoming games (new templates; classic has
     *  none). Which bucket a game is in is core time-zone formatting. */
    when: l.when,

    // Registration and member-account pages (registration rebuild): the
    // site-core catalogue's member group (#1448, #1464).
    account: mm.account,
    myAccount: mm.myAccount,
    recovery: {
      ...mm.recovery,
      /** recovery.sentBody in three parts, so a view can bold the address. */
      sentBodyParts: (email: string): [string, string, string] => {
        const s = mm.recovery.sentBody(email)
        const at = s.indexOf(email)
        return at < 0 ? [s, '', ''] : [s.slice(0, at), email, s.slice(at + email.length)]
      },
    },
    registration: mm.registration,
    membersOnly: mm.membersOnly,
    join: mm.join,
  }
}

export type UiStrings = ReturnType<typeof buildUi>
